import React, { useContext, useState } from "react";
import { DataLayerContext } from "../dataLayer/DataLayer";
import "./playerStatsScreen.css";
import { AppBar } from "./components/appBar";
import { PageHeading } from "./components/pageHeading";
import { IPlayerStat, IPlayerStats } from "../dataLayer/v1/interfaces/data/IPlayerStats";
import { SimpleDate } from "../dataLayer/SimpleDate";
import { numberToOrdinalString } from "../helpers/numberToOrdinalString";
import { useObservableProperty } from "../observable/useObservableProperty";
import { PremiumAccessCard } from "./components/premiumAccessCard";

function shortDate(d: SimpleDate): string {
  return new Date(d.year, d.month - 1, d.day).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function PlayerStatsScreen() {
  const dataLayer = useContext(DataLayerContext);
  const [playerStats, setPlayerStats] = useState<IPlayerStats | undefined>();
  const premium = useObservableProperty(dataLayer.premium);

  React.useEffect(() => {
    dataLayer
      .getPlayerStats()
      .then((playerStatsFromDataLayer: IPlayerStats | undefined) =>
        setPlayerStats(playerStatsFromDataLayer),
      );
  }, [dataLayer, setPlayerStats]);

  React.useEffect(() => {
    if (premium.status === "idle") {
      void dataLayer.loadPremiumStatus();
    }
  }, [dataLayer, premium.status]);

  const onBackClicked = () => {
    dataLayer.currentPuzzle.value = undefined;
    dataLayer.navigateBack();
  };

  const onNavigateToPuzzleByDate = (date: SimpleDate) => {
    dataLayer.currentPuzzle.value = dataLayer.findPuzzleByDate(date);
    dataLayer.navigateTo("ReviewPuzzle");
  };

  return (
    <div className="screen stats">
      <AppBar onBack={onBackClicked} />
      <PageHeading eyebrow="Your progress" title="Stats" />

      <div className="stats__body">
        {playerStats && (
          <div className="stats__hl">
            <Highlight label="Total points" stat={playerStats.totalPoints} accent />
            <Highlight label="Total words" stat={playerStats.mostFoundWords} />
          </div>
        )}

        <div className="stats__sec-head">Personal records</div>
        <div className="stats__sec-sub">Rank is out of all community plays</div>

        <table className="records">
          <thead>
            <tr>
              <th>Record</th>
              <th>Best</th>
              <th>Rank</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {row("Total points", playerStats?.totalPoints, onNavigateToPuzzleByDate)}
            {row("Total words", playerStats?.mostFoundWords, onNavigateToPuzzleByDate)}
            {row("Regular points", playerStats?.regularPoints, onNavigateToPuzzleByDate)}
            {row("Rarity points", playerStats?.rarityPoints, onNavigateToPuzzleByDate)}
            {premium.tier && lengthRows(playerStats, onNavigateToPuzzleByDate)}
          </tbody>
        </table>
        {!premium.tier && <PremiumAccessCard context="stats" />}
      </div>
    </div>
  );
}

function Highlight({
  label,
  stat,
  accent,
}: {
  label: string;
  stat: IPlayerStat | undefined;
  accent?: boolean;
}) {
  return (
    <div className="hlitem">
      <div className={`hlitem__val num${accent ? " accent" : ""}`}>
        {stat ? stat.value.toLocaleString() : "\u2014"}
      </div>
      <div className="hlitem__label">{label}</div>
    </div>
  );
}

function lengthRows(playerStats: IPlayerStats | undefined, onNavigate: (date: SimpleDate) => void) {
  if (!playerStats) return <></>;
  const rows: JSX.Element[] = [];
  // letters[N] holds the record for exactly N-letter words (see mapBackendStats).
  // Each length is tracked separately, so we render one row per length, longest
  // first, down to the shortest legal word (3 letters).
  const longest = playerStats.letters.length - 1;
  for (let len = longest; len >= 3; len--) {
    const stat = playerStats.letters[len];
    if (stat) {
      rows.push(row(`${len}-letter word`, stat, onNavigate));
    }
  }
  return <>{rows}</>;
}

function row(
  title: string,
  stat: IPlayerStat | undefined,
  onNavigate: (date: SimpleDate) => void,
): JSX.Element {
  return (
    <tr key={title}>
      <td>{title}</td>
      <td className="v">{stat ? stat.value.toLocaleString() : ""}</td>
      <td className="rank">
        {stat
          ? stat.placeOverall === undefined
            ? "Pending"
            : numberToOrdinalString(stat.placeOverall)
          : ""}
      </td>
      <td>
        {stat ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onNavigate(stat.date);
            }}
          >
            {shortDate(stat.date)}
          </a>
        ) : (
          ""
        )}
      </td>
    </tr>
  );
}
